import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    FeeAmendmentRequest,
    GlobalAccount,
    GlobalAccountFeeRule,
    GlobalAccountSettlementRule,
    GUID,
    NewFeeRequest,
    PortfolioFeeRule,
    PortfolioSettlementRule,
} from '@ndvr/billing-api';

import { BillingApi } from 'API';
import { Toast } from 'widgets';

export const useGlobalAccountFeeRuleTimeline = (globalAccountGUID?: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'billing', 'feeRuleTimeline'],
        queryFn: async () => {
            const [response] = await BillingApi().getGlobalAccountFeeRuleTimeline(globalAccountGUID!);
            return response ?? undefined;
        },
        enabled: !!globalAccountGUID,
    });
};

export const useSaveGlobalAccountFeeRule = (globalAccountGUID: GUID<GlobalAccount>, editing: boolean) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (globalAccountFeeRule: GlobalAccountFeeRule) => {
            return editing
                ? BillingApi({ hideError: true }).updateGlobalAccountFeeRule(globalAccountFeeRule)
                : BillingApi({ hideError: true }).createGlobalAccountFeeRule(globalAccountFeeRule);
        },
        onSuccess: () => {
            Toast.success(`Pricing profile ${editing ? 'updated' : 'created'}`);
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'billing'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to save pricing profile'),
    });
};

export const useDeleteGlobalAccountFeeRule = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (globalAccountRuleGUID: GUID<GlobalAccountFeeRule>) => {
            return BillingApi({ hideError: true }).deleteGlobalAccountFeeRule(globalAccountRuleGUID);
        },
        onSuccess: () => {
            Toast.success('Pricing profile deleted');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'billing'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to delete pricing profile'),
    });
};

export const useGlobalAccountSettlementRuleTimeline = (globalAccountGUID?: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'billing', 'settlementRuleTimeline'],
        queryFn: async () => {
            const [response] = await BillingApi().getGlobalAccountSettlementRuleTimeline(globalAccountGUID!);
            return response ?? undefined;
        },
        enabled: !!globalAccountGUID,
    });
};

export const useSaveGlobalAccountSettlementRule = (globalAccountGUID: GUID<GlobalAccount>, editing: boolean) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (globalAccountSettlementRule: GlobalAccountSettlementRule) => {
            return editing
                ? BillingApi({ hideError: true }).updateGlobalAccountSettlementRule(globalAccountSettlementRule)
                : BillingApi({ hideError: true }).createGlobalAccountSettlementRule(globalAccountSettlementRule);
        },
        onSuccess: () => {
            Toast.success(`Settlement profile ${editing ? 'updated' : 'created'}`);
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'billing'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to save settlement profile'),
    });
};

export const useDeleteGlobalAccountSettlementRule = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (globalAccountRuleGUID: GUID<GlobalAccountSettlementRule>) => {
            return BillingApi({ hideError: true }).deleteGlobalAccountSettlementRule(globalAccountRuleGUID);
        },
        onSuccess: () => {
            Toast.success('Settlement profile deleted');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'billing'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to delete settlement profile'),
    });
};

export const usePortfolioFeeRuleTimeline = (globalAccountGUID?: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'billing', 'portfolioFeeRuleTimeline'],
        queryFn: async () => {
            const [response] = await BillingApi().getPortfolioFeeRuleTimeline(globalAccountGUID!);
            return response ?? undefined;
        },
        enabled: !!globalAccountGUID,
    });
};

export const useSavePortfolioFeeRule = (globalAccountGUID: GUID<GlobalAccount>, editing: boolean) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (portfolioFeeRule: PortfolioFeeRule) => {
            return editing
                ? BillingApi({ hideError: true }).updatePortfolioFeeRule(portfolioFeeRule)
                : BillingApi({ hideError: true }).createPortfolioFeeRule(portfolioFeeRule);
        },
        onSuccess: () => {
            Toast.success(`Pricing override ${editing ? 'updated' : 'created'}`);
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'billing'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to save pricing override'),
    });
};

export const useDeletePortfolioFeeRule = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (portfolioFeeRuleGUID: GUID<PortfolioFeeRule>) => {
            return BillingApi({ hideError: true }).deletePortfolioFeeRule(portfolioFeeRuleGUID);
        },
        onSuccess: () => {
            Toast.success('Pricing override deleted');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'billing'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to delete pricing override'),
    });
};

export const usePortfolioSettlementRuleTimeline = (globalAccountGUID?: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'billing', 'portfolioSettlementRuleTimeline'],
        queryFn: async () => {
            const [response] = await BillingApi().getPortfolioSettlementRuleTimeline(globalAccountGUID!);
            return response ?? undefined;
        },
        enabled: !!globalAccountGUID,
    });
};

export const useSavePortfolioSettlementRule = (globalAccountGUID: GUID<GlobalAccount>, editing: boolean) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (portfolioSettlementRule: PortfolioSettlementRule) => {
            return editing
                ? BillingApi({ hideError: true }).updatePortfolioSettlementRule(portfolioSettlementRule)
                : BillingApi({ hideError: true }).createPortfolioSettlementRule(portfolioSettlementRule);
        },
        onSuccess: () => {
            Toast.success(`Settlement override ${editing ? 'updated' : 'created'}`);
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'billing'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to save settlement override'),
    });
};

export const useDeletePortfolioSettlementRule = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (portfolioSettlementRuleGUID: GUID<PortfolioSettlementRule>) => {
            return BillingApi({ hideError: true }).deletePortfolioSettlementRule(portfolioSettlementRuleGUID);
        },
        onSuccess: () => {
            Toast.success('Settlement override deleted');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'billing'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to delete settlement override'),
    });
};

export const usePricingPackages = () => {
    return useQuery({
        queryKey: ['pricingPackage', 'list'],
        queryFn: async () => {
            const [response] = await BillingApi().getPricingPackages();
            return response ?? undefined;
        },
    });
};

export const useFeeDetails = (globalAccountGUID: GUID<GlobalAccount>) =>
    useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'feeDetails'],
        queryFn: async () => {
            const [response] = await BillingApi().getFeeDetailsForGlobalAccount(globalAccountGUID);
            return response ?? undefined;
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

export const useCreateNewFee = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (feeRequest: NewFeeRequest) => BillingApi().applyNewFee(feeRequest),
        onSuccess: () => {
            Toast.success('Fee created');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'feeDetails'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to create fee'),
    });
};

export const useCreateFeeAmendment = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (feeAmendmentRequest: FeeAmendmentRequest) =>
            BillingApi().applyFeeAmendment(feeAmendmentRequest),
        onSuccess: () => {
            Toast.success('Fee amendment created');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'feeDetails'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to create fee amendment'),
    });
};
